import noop from 'lodash/noop'
import omit from 'lodash/omit'
import omitBy from 'lodash/omitBy'
import split from 'lodash/split'
import { useRouter } from 'next/router'
import React, { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import { NativeModal, NativeModalBody, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { PaymentError } from '@Domains/Checkout/Steps/Payment/Components/PaymentError/PaymentError'
import { useTranslation } from '@Globals/Translations'

interface CheckoutModalProviderProps {
  isShown: boolean
  setModal: Dispatch<SetStateAction<ReactNode | null>>
  unSetModal: () => void

}

export const CheckoutModalContext = createContext<CheckoutModalProviderProps>({
  isShown: false,
  setModal: noop,
  unSetModal: noop,
})

export const CheckoutModalProvider: FC<{ children?: React.ReactNode, onClosedModal?: () => void }> = ({
  children,
  onClosedModal,
}) => {
  const {
    asPath,
    locale = '',
    pathname,
    query,
    replace,
  } = useRouter()
  const [openModal, setOpenModal] = useState(false)
  const [modal, setModal] = useState<ReactNode | null>(null)
  const [queryError, setQueryError] = useState(false)
  const [firstRender, setFirstRender] = useState(true)
  const { t } = useTranslation()

  const unSetModal = useCallback(() => {
    setModal(null)
    setOpenModal(false)
  }, [setModal])

  useEffect(() => {
    if (modal) {
      setOpenModal(true)
    } else {
      setOpenModal(false)
    }
  }, [modal])

  useEffect(() => {
    if (!openModal && onClosedModal && !firstRender) {
      onClosedModal()
    }
    setFirstRender(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal])

  const removeFailed = useCallback(() => {
    const nextQuery = omitBy(query, (_, queryKey) => queryKey === 'failed')
    replace(
      {
        pathname,
        query: nextQuery,
      },
      {
        pathname: split(asPath, '?')[0],
        query: omit(nextQuery, ['slug']),
      },
      {
        locale,
        shallow: true,
        scroll: false,
      },
    )
  }, [asPath, locale, pathname, query, replace])

  useEffect(() => {
    if (query.failed && !queryError) {
      setQueryError(true)
      removeFailed()
      setModal(<PaymentError
        closeModal={() => unSetModal()}
        error={t('common:SOMETHING_WENT_WRONG_PLEASE_CONTACT_US')}
      />)
    }
  }, [query.failed, queryError, removeFailed, t, unSetModal])

  const value = useMemo(() => ({
    setModal,
    unSetModal,
    isShown: !!modal,
  }), [modal, unSetModal])
  return (
    <CheckoutModalContext.Provider value={value}>
      {modal && (
        <NativeModal backdrop closeOnBackDrop={false} closeOnEscape={false} defaultOpen={openModal} onClose={() => unSetModal()}>
          <NativeModalHeader />
          <NativeModalBody>
            {modal}
          </NativeModalBody>
        </NativeModal>
      )}
      {children}
    </CheckoutModalContext.Provider>
  )
}
